import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Switch,
    TextField,
    Autocomplete,
    CircularProgress,
    Divider,
    Alert,
    Chip,
    Tabs,
    Tab,
    FormControlLabel,
    Checkbox,
    Tooltip,
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    DeleteOutline as DeleteIcon,
    Add as AddIcon,
    RestaurantOutlined as RestaurantIcon,
    LocalCafeOutlined as DrinkIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    ChevronLeft as PreviousIcon,
    ChevronRight as NextIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import swapSetsApi from "../../api/swapSets";
import mealsApi from "../../api/meals";
import drinksApi from "../../api/drinks";
import Layout from "../../components/layout/Layout";
import ExtraInstantMeals from "./ExtraInstantMeals";
import { InstantSwapEntry } from "../../api/swapSets";

interface Meal {
    _id: string;
    name: string;
    imageUrl: string;
    serialNumber: string;
}

interface SwapSetMeal {
    mealId: Meal;
    active: boolean;
    _id?: string;
}

interface Drink {
    _id: string;
    name: string;
    type: "coffee" | "blend";
    imageUrl?: string;
}

interface SwapSetDrink {
    drinkId: Drink;
    isDefault: boolean;
    active: boolean;
    _id?: string;
}

interface SwapSet {
    _id: string;
    serialNumber: number;
    active: boolean;
    meals: SwapSetMeal[];
    drinks?: SwapSetDrink[];
    instantMeals?: InstantSwapEntry[];
}

const SwapSetDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [swapSet, setSwapSet] = useState<SwapSet | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<"meals" | "drinks" | "instant">("meals");

    // For adding new meals
    const [allMeals, setAllMeals] = useState<Meal[]>([]);
    const [selectedMeal, setSelectedMeal] = useState<Meal | null>(null);
    const [loadingMeals, setLoadingMeals] = useState(false);

    // For adding new drinks
    const [allDrinks, setAllDrinks] = useState<Drink[]>([]);
    const [selectedDrink, setSelectedDrink] = useState<Drink | null>(null);
    const [isDefaultDrink, setIsDefaultDrink] = useState(false);

    // For navigation
    const [allSwapSets, setAllSwapSets] = useState<SwapSet[]>([]);

    useEffect(() => {
        fetchSwapSet();
    }, [id]);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchSwapSet = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const data = await swapSetsApi.getSwapSetById(id);
            setSwapSet(data);
        } catch (err: any) {
            setError(err.message || "Error fetching swap set data");
        } finally {
            setLoading(false);
        }
    };

    const fetchInitialData = async () => {
        setLoadingMeals(true);
        try {
            const [mealsData, drinksData, swapSetsData] = await Promise.all([
                mealsApi.getMeals({ limit: 1000 }),
                drinksApi.getDrinks(),
                swapSetsApi.getSwapSets(),
            ]);
            setAllMeals(mealsData.meals || []);
            setAllDrinks(drinksData.drinks || []);
            setAllSwapSets(swapSetsData.sort((a: any, b: any) => a.serialNumber - b.serialNumber));
        } catch (err: any) {
            setError(err.message || "Error fetching initial data");
        } finally {
            setLoadingMeals(false);
        }
    };

    // Available meals not yet in this swap set
    const availableMeals = React.useMemo(() => {
        if (!swapSet) return [];
        const existingMealIds = new Set(swapSet.meals.map((m) => m.mealId?._id));
        return allMeals
            .filter((meal) => !existingMealIds.has(meal._id))
            .sort((a, b) => parseInt(a.serialNumber || "0") - parseInt(b.serialNumber || "0"));
    }, [allMeals, swapSet]);

    // Available drinks not yet in this swap set
    const availableDrinks = React.useMemo(() => {
        if (!swapSet) return [];
        const existingDrinkIds = new Set((swapSet.drinks || []).map((d) => d.drinkId?._id));
        return allDrinks
            .filter((drink) => !existingDrinkIds.has(drink._id))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [allDrinks, swapSet]);

    const handleToggleSetActive = async () => {
        if (!swapSet) return;
        try {
            const updated = await swapSetsApi.updateSwapSet(swapSet._id, {
                active: !swapSet.active,
            });
            setSwapSet(updated);
        } catch (err: any) {
            setError("Failed to update status");
        }
    };

    // ── Meal Operations ──────────────────────────────────────────
    const handleAddMeal = async () => {
        if (!swapSet || !selectedMeal) return;

        const exists = swapSet.meals.some((m) => m.mealId?._id === selectedMeal._id);
        if (exists) {
            alert("Meal already in this set");
            return;
        }

        const newMealsList = [
            ...swapSet.meals.map((m) => ({ mealId: m.mealId._id, active: m.active })),
            { mealId: selectedMeal._id, active: true },
        ];

        try {
            const updated = await swapSetsApi.updateSwapSet(swapSet._id, { meals: newMealsList });
            setSwapSet(updated);
            setSelectedMeal(null);
        } catch (err) {
            setError("Failed to add meal");
        }
    };

    const handleRemoveMeal = async (mealIdToRemove: string) => {
        if (!swapSet) return;
        const newMealsList = swapSet.meals
            .filter((m) => m.mealId?._id !== mealIdToRemove)
            .map((m) => ({ mealId: m.mealId._id, active: m.active }));

        try {
            const updated = await swapSetsApi.updateSwapSet(swapSet._id, { meals: newMealsList });
            setSwapSet(updated);
        } catch (err) {
            setError("Failed to remove meal");
        }
    };

    const handleToggleMealActive = async (mealId: string, currentStatus: boolean) => {
        if (!swapSet) return;
        const newMealsList = swapSet.meals.map((m) => ({
            mealId: m.mealId._id,
            active: m.mealId._id === mealId ? !currentStatus : m.active,
        }));

        try {
            const updated = await swapSetsApi.updateSwapSet(swapSet._id, { meals: newMealsList });
            setSwapSet(updated);
        } catch (err) {
            setError("Failed to update meal status");
        }
    };

    // ── Drink Operations ─────────────────────────────────────────
    const handleAddDrink = async () => {
        if (!swapSet || !selectedDrink) return;

        const currentDrinks = swapSet.drinks || [];
        const exists = currentDrinks.some((d) => d.drinkId?._id === selectedDrink._id);
        if (exists) {
            alert("Drink already in this set");
            return;
        }

        const newDrinksList = [
            ...currentDrinks.map((d) => ({
                drinkId: d.drinkId._id,
                isDefault: d.isDefault,
                active: d.active,
            })),
            {
                drinkId: selectedDrink._id,
                isDefault: isDefaultDrink,
                active: true,
            },
        ];

        try {
            const updated = await swapSetsApi.updateSwapSet(swapSet._id, { drinks: newDrinksList });
            setSwapSet(updated);
            setSelectedDrink(null);
            setIsDefaultDrink(false);
        } catch (err) {
            setError("Failed to add drink");
        }
    };

    const handleRemoveDrink = async (drinkIdToRemove: string) => {
        if (!swapSet) return;
        const newDrinksList = (swapSet.drinks || [])
            .filter((d) => d.drinkId?._id !== drinkIdToRemove)
            .map((d) => ({
                drinkId: d.drinkId._id,
                isDefault: d.isDefault,
                active: d.active,
            }));

        try {
            const updated = await swapSetsApi.updateSwapSet(swapSet._id, { drinks: newDrinksList });
            setSwapSet(updated);
        } catch (err) {
            setError("Failed to remove drink");
        }
    };

    const handleToggleDrinkActive = async (drinkId: string, currentStatus: boolean) => {
        if (!swapSet) return;
        const newDrinksList = (swapSet.drinks || []).map((d) => ({
            drinkId: d.drinkId._id,
            isDefault: d.isDefault,
            active: d.drinkId._id === drinkId ? !currentStatus : d.active,
        }));

        try {
            const updated = await swapSetsApi.updateSwapSet(swapSet._id, { drinks: newDrinksList });
            setSwapSet(updated);
        } catch (err) {
            setError("Failed to update drink status");
        }
    };

    const handleToggleDrinkDefault = async (drinkId: string, currentIsDefault: boolean) => {
        if (!swapSet) return;
        const newDrinksList = (swapSet.drinks || []).map((d) => ({
            drinkId: d.drinkId._id,
            isDefault: d.drinkId._id === drinkId ? !currentIsDefault : d.isDefault,
            active: d.active,
        }));

        try {
            const updated = await swapSetsApi.updateSwapSet(swapSet._id, { drinks: newDrinksList });
            setSwapSet(updated);
        } catch (err) {
            setError("Failed to toggle default drink");
        }
    };

    if (loading) return <Layout><Box role="status" aria-label="Loading swap-set" sx={{ p: 4, display: "flex", justifyContent: "center" }}><CircularProgress sx={{ color: '#f97316' }} /></Box></Layout>;
    if (!swapSet) return <Layout><Alert severity="error">Swap Set not found</Alert></Layout>;

    const currentIdx = allSwapSets.findIndex((s) => s._id === swapSet._id);
    const prevSwapSetId = currentIdx > 0 ? allSwapSets[currentIdx - 1]._id : null;
    const nextSwapSetId = currentIdx < allSwapSets.length - 1 ? allSwapSets[currentIdx + 1]._id : null;

    const setDrinks = swapSet.drinks || [];
    const coffeeInSet = setDrinks.filter((d) => d.drinkId?.type === "coffee").length;
    const blendInSet = setDrinks.filter((d) => d.drinkId?.type === "blend").length;

    return (
        <Layout>
            <Box sx={{ '& .MuiPaper-root': { borderRadius: '8px', boxShadow: 'none', border: '1px solid #e4e7ec' }, '& .MuiButton-contained': { backgroundImage: 'none' }, '& .MuiButton-contained:not(.Mui-disabled)': { bgcolor: '#f97316', '&:hover': { bgcolor: '#ea580c', backgroundImage: 'none' } }, '& .MuiButton-contained.Mui-disabled': { bgcolor: '#f2f4f7', color: '#98a2b3' } }}>
                {/* Header Navigation */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Tooltip title="Back to swap sets"><IconButton
                            aria-label="Back to swap sets"
                            onClick={() => navigate("/swap-sets")}
                            sx={{ color: "text.secondary" }}
                        >
                            <ArrowBackIcon />
                        </IconButton></Tooltip>

                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="Previous swap-set"><span><IconButton
                                aria-label="Previous swap-set"
                                disabled={!prevSwapSetId}
                                onClick={() => navigate(`/swap-sets/${prevSwapSetId}`)}
                            >
                                <PreviousIcon />
                            </IconButton></span></Tooltip>
                            <Tooltip title="Next swap-set"><span><IconButton
                                aria-label="Next swap-set"
                                disabled={!nextSwapSetId}
                                onClick={() => navigate(`/swap-sets/${nextSwapSetId}`)}
                            >
                                <NextIcon />
                            </IconButton></span></Tooltip>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography variant="h4" fontWeight="700" sx={{ color: "#1a1a1a", mb: 1 }}>
                                Swap-set {swapSet.serialNumber}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", flexWrap: 'wrap', gap: 1 }}>
                                <Chip
                                    label={swapSet.active ? "Active" : "Inactive"}
                                    size="small"
                                    sx={{
                                        borderRadius: 1,
                                        bgcolor: swapSet.active ? "#e6f4ea" : "#f1f3f4",
                                        color: swapSet.active ? "#1e8e3e" : "#5f6368",
                                        fontWeight: 600,
                                    }}
                                />
                                <Typography variant="body2" color="textSecondary">
                                    • {swapSet.meals.length} Meals • {setDrinks.length} Drinks ({coffeeInSet} Coffees, {blendInSet} Blends)
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography variant="body2" fontWeight="500" color="textSecondary">
                                Set Active
                            </Typography>
                            <Switch checked={swapSet.active} onChange={handleToggleSetActive} size="medium" inputProps={{ 'aria-label': 'Set active' }} />
                        </Box>
                    </Box>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

                {/* Tabs to switch between Meals and Drinks */}
                <Box sx={{ mb: 3, borderBottom: '1px solid #e4e7ec' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, val) => setActiveTab(val)}
                        textColor="primary"
                        indicatorColor="primary"
                    >
                        <Tab
                            icon={<RestaurantIcon />}
                            iconPosition="start"
                            label={`Meals (${swapSet.meals.length})`}
                            value="meals"
                            sx={{ fontWeight: 600, textTransform: "none" }}
                        />
                        <Tab
                            icon={<DrinkIcon />}
                            iconPosition="start"
                            label={`Drinks (${setDrinks.length})`}
                            value="drinks"
                            sx={{ fontWeight: 600, textTransform: "none" }}
                        />
                        <Tab label={`Extra Instant Meals (${swapSet.instantMeals?.length || 0})`} value="instant" sx={{ fontWeight: 600, textTransform: "none" }} />
                    </Tabs>
                </Box>

                {/* ── MEALS TAB ────────────────────────────────────────────── */}
                {activeTab === "instant" && <ExtraInstantMeals key={swapSet._id} swapSetId={swapSet._id} entries={swapSet.instantMeals || []} onUpdated={setSwapSet} />}
                {activeTab === "meals" && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        {/* Left: Add Meal */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>

                                <Autocomplete
                                    options={availableMeals}
                                    getOptionLabel={(option) => `${option.name} (#${option.serialNumber})`}
                                    value={selectedMeal}
                                    onChange={(event, newValue) => setSelectedMeal(newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Select Meal" placeholder="Search..." fullWidth size="small" />
                                    )}
                                    loading={loadingMeals}
                                    sx={{ flex: '1 1 280px', maxWidth: { sm: 520 }, minWidth: 0 }}
                                />

                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    disabled={!selectedMeal}
                                    onClick={handleAddMeal}
                                    sx={{ textTransform: "none", minHeight: 40 }}
                                >
                                    Add to Set
                                </Button>
                            </Box>
                        </Box>

                        {/* Right: Meals List */}
                        <Box sx={{ flexGrow: 1 }}>
                            <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #f0f0f0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                                <Typography variant="h6" fontWeight="700" gutterBottom>
                                    Configured Meals ({swapSet.meals.length})
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                <List disablePadding>
                                    {swapSet.meals.map((item, index) => (
                                        <React.Fragment key={item.mealId._id}>
                                            {index > 0 && <Divider />}
                                            <ListItem sx={{ py: 1.5, px: 0, flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1 }}>
                                                <ListItemAvatar>
                                                    <Avatar
                                                        src={item.mealId.imageUrl}
                                                        variant="rounded"
                                                        sx={{ width: 48, height: 48, mr: 1, bgcolor: "#f8f9fa" }}
                                                    />
                                                </ListItemAvatar>
                                                <ListItemText sx={{ minWidth: 0, flex: '1 1 160px', overflowWrap: 'anywhere' }}
                                                    primary={
                                                        <Typography variant="subtitle1" fontWeight="600" sx={{ color: item.active ? "#1a1a1a" : "#9ca3af" }}>
                                                            {item.mealId.name}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography variant="body2" color="textSecondary">
                                                            #{item.mealId.serialNumber} · {item.active ? 'Active' : 'Inactive'}
                                                        </Typography>
                                                    }
                                                />
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                                                    <Switch
                                                        checked={item.active}
                                                        onChange={() => handleToggleMealActive(item.mealId._id, item.active)}
                                                        size="small"
                                                        color="success"
                                                        inputProps={{ 'aria-label': `Enable ${item.mealId.name}` }}
                                                    />
                                                    <IconButton
                                                        aria-label={`Remove ${item.mealId.name}`}
                                                        title={`Remove ${item.mealId.name}`}
                                                        onClick={() => handleRemoveMeal(item.mealId._id)}
                                                        size="small"
                                                        sx={{ color: "#9ca3af", "&:hover": { color: "#ef4444", bgcolor: "#fef2f2" } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </ListItem>
                                        </React.Fragment>
                                    ))}
                                    {swapSet.meals.length === 0 && (
                                        <Box sx={{ textAlign: "center", py: 8 }}>
                                            <Typography variant="body1" color="textSecondary">
                                                No meals configured in this set.
                                            </Typography>
                                        </Box>
                                    )}
                                </List>
                            </Paper>
                        </Box>
                    </Box>
                )}

                {/* ── DRINKS TAB ───────────────────────────────────────────── */}
                {activeTab === "drinks" && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        {/* Left: Add Drink */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>

                                <Autocomplete
                                    options={availableDrinks}
                                    getOptionLabel={(option) => `${option.name} (${option.type === "coffee" ? "Coffee" : "Blend"})`}
                                    value={selectedDrink}
                                    onChange={(event, newValue) => setSelectedDrink(newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Select Drink" placeholder="Search coffee or blend..." fullWidth size="small" />
                                    )}
                                    sx={{ flex: '1 1 280px', maxWidth: { sm: 520 }, minWidth: 0 }}
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isDefaultDrink}
                                            onChange={(e) => setIsDefaultDrink(e.target.checked)}
                                        />
                                    }
                                    label="Default drink"
                                    sx={{ mr: 0 }}
                                />

                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    disabled={!selectedDrink}
                                    onClick={handleAddDrink}
                                    sx={{ textTransform: "none", minHeight: 40 }}
                                >
                                    Add to Set
                                </Button>
                            </Box>
                        </Box>

                        {/* Right: Drinks List */}
                        <Box sx={{ flexGrow: 1 }}>
                            <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #f0f0f0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                                <Typography variant="h6" fontWeight="700" gutterBottom>
                                    Configured Drinks ({setDrinks.length})
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                <List disablePadding>
                                    {setDrinks.map((item, index) => (
                                        <React.Fragment key={item.drinkId?._id}>
                                            {index > 0 && <Divider />}
                                            <ListItem sx={{ py: 1.5, px: 0, flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1 }}>
                                                <ListItemAvatar>
                                                    <Avatar
                                                        src={item.drinkId?.imageUrl}
                                                        variant="rounded"
                                                        sx={{ width: 48, height: 48, mr: 1, bgcolor: "#f8f9fa" }}
                                                    />
                                                </ListItemAvatar>
                                                <ListItemText sx={{ minWidth: 0, flex: '1 1 160px', overflowWrap: 'anywhere' }}
                                                    primary={
                                                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: 'wrap', gap: 1 }}>
                                                            <Typography variant="subtitle1" fontWeight="600" sx={{ color: item.active ? "#1a1a1a" : "#9ca3af" }}>
                                                                {item.drinkId?.name}
                                                            </Typography>
                                                            {item.isDefault && (
                                                                <Chip
                                                                    label="Default"
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{ fontSize: "0.7rem", height: 20 }}
                                                                />
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography variant="body2" color="textSecondary" sx={{ textTransform: "capitalize" }}>
                                                            {item.drinkId?.type === "coffee" ? "Coffee" : "Blend / Shake"} · {item.active ? 'Active' : 'Inactive'}
                                                        </Typography>
                                                    }
                                                />
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                    <Tooltip title={item.isDefault ? "Remove Default Flag" : "Mark as Default"}>
                                                        <IconButton
                                                            aria-label={item.isDefault ? `Remove default for ${item.drinkId?.name}` : `Make ${item.drinkId?.name} default`}
                                                            size="small"
                                                            onClick={() => handleToggleDrinkDefault(item.drinkId._id, item.isDefault)}
                                                            color={item.isDefault ? "primary" : "default"}
                                                        >
                                                            {item.isDefault ? <StarIcon /> : <StarBorderIcon />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Switch
                                                        checked={item.active}
                                                        onChange={() => handleToggleDrinkActive(item.drinkId._id, item.active)}
                                                        size="small"
                                                        color="success"
                                                        inputProps={{ 'aria-label': `Enable ${item.drinkId?.name}` }}
                                                    />
                                                    <IconButton
                                                        aria-label={`Remove ${item.drinkId?.name}`}
                                                        title={`Remove ${item.drinkId?.name}`}
                                                        onClick={() => handleRemoveDrink(item.drinkId._id)}
                                                        size="small"
                                                        sx={{ color: "#9ca3af", "&:hover": { color: "#ef4444", bgcolor: "#fef2f2" } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </ListItem>
                                        </React.Fragment>
                                    ))}
                                    {setDrinks.length === 0 && (
                                        <Box sx={{ textAlign: "center", py: 8 }}>
                                            <Typography variant="body1" color="textSecondary">
                                                No drinks configured in this set.
                                            </Typography>
                                        </Box>
                                    )}
                                </List>
                            </Paper>
                        </Box>
                    </Box>
                )}
            </Box>
        </Layout>
    );
};

export default SwapSetDetail;
